import i18n from "@/i18n";
import ResponsiveModal from "./ResponsiveModal";
import { Input } from "./ui/input";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import {
  Cake,
  ChevronDownIcon,
  Loader2,
  Mail,
  Phone,
  User,
} from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { UserDetailsType } from "@/types/user";
import { parse } from "date-fns";

interface UserDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  fullName?: string;
  email?: string;
  phone?: string;
  birthdate?: string;
  showMissingFieldsOnly?: boolean;
  showSkipButton?: boolean;
}

export default function UserDetailsModal({
  open,
  onClose,
  onSubmit,
  fullName,
  email,
  phone,
  birthdate,
  showMissingFieldsOnly = false,
  showSkipButton = false,
}: UserDetailsModalProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm();

  useEffect(() => {
    setValue("name", fullName);
    setValue("email", email);
    setValue("phone", phone);

    if (birthdate) {
      const [day, month, year] = birthdate?.split("/").map(Number) ?? [];
      const date = new Date(year, month - 1, day);
      setValue("birthdate", date);
    }
  }, [fullName, email, phone, birthdate]);

  const [openDate, setOpenDate] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const submit = async (data: any) => {
    setLoading(true);
    await onSubmit(data);
    setLoading(false);
  };

  const getColor = (field: string) => {
    if (field === "name") return "blue";
    if (field === "email") return "green";
    if (field === "phone") return "red";
    if (field === "birthdate") return "purple";
    return "blue";
  };

  const getIcon = (field: string) => {
    if (field === "name")
      return <User className={`w-4 h-4 text-${getColor(field)}-500`} />;
    if (field === "email")
      return <Mail className={`w-4 h-4 text-${getColor(field)}-500`} />;
    if (field === "phone")
      return <Phone className={`w-4 h-4 text-${getColor(field)}-500`} />;
    if (field === "birthdate")
      return <Cake className={`w-4 h-4 text-${getColor(field)}-500`} />;
    return <User className={`w-4 h-4 text-${getColor(field)}-500`} />;
  };

  const renderContent = () => {
    return (
      <form
        dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
        className="flex flex-col min-h-[400px] max-h-[85vh] p-6 sm:p-8 gap-4"
        onSubmit={handleSubmit(submit)}
      >
        {/* Header */}
        <div className="text-center space-y-2 shrink-0">
          <h1 className="text-2xl font-bold">Please fill in your details</h1>
        </div>

        {/* {fieldsToShow.map((field) => (
          <div className="flex flex-col gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <div
                className={`p-2 bg-${getColor(
                  field
                )}-50 rounded-lg flex items-center justify-center`}
              >
                {getIcon(field)}
              </div>
              <h4 className="text-md font-semibold">{field}</h4>
            </div>
            <Input
              autoComplete="off"
              placeholder={`Enter ${field}`}
              {...register(field)}
              className={`${errors.name ? "border-red-500" : ""}`}
            />
            {errors[field] && (
              <p className="text-red-500 text-sm">
                {errors[field].message as string}
              </p>
            )}
          </div>
        ))} */}

        {/* Full Name */}
        {(!showMissingFieldsOnly || !fullName) && (
          <div className="flex flex-col gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <div
                className={`p-2 bg-blue-50 rounded-lg flex items-center justify-center`}
              >
                <User className="w-4 h-4 text-blue-500" />
              </div>
              <h4 className="text-md font-semibold">Full Name</h4>
            </div>
            <Input
              autoComplete="off"
              placeholder="Enter Full Name"
              {...register("name", {
                minLength: {
                  value: 3,
                  message: "Name must be at least 3 characters long",
                },
              })}
              className={`${errors.name ? "border-red-500" : ""}`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm">
                {errors.name.message as string}
              </p>
            )}
          </div>
        )}

        {(!showMissingFieldsOnly || !email) && (
          <div className="flex flex-col gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <div
                className={`p-2 bg-green-50 rounded-lg flex items-center justify-center`}
              >
                <Mail className="w-4 h-4 text-green-500" />
              </div>
              <h4 className="text-md font-semibold">Email</h4>
            </div>
            <Input
              autoComplete="off"
              placeholder="Enter Email"
              className={`${errors.email ? "border-red-500" : ""}`}
              {...register("email", {
                pattern: {
                  value: /^[^@ ]+@[^@ ]+\.[^@ ]+$/,
                  message: "Invalid email address",
                },
              })}
            />
            {errors.email && (
              <p className="text-red-500 text-sm">
                {errors.email.message as string}
              </p>
            )}
          </div>
        )}

        {(!showMissingFieldsOnly || !phone) && (
          <div className="flex flex-col gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <div
                className={`p-2 bg-red-50 rounded-lg flex items-center justify-center`}
              >
                <Phone className="w-4 h-4 text-red-500" />
              </div>
              <h4 className="text-md font-semibold">Phone</h4>
            </div>
            <Input
              autoComplete="off"
              placeholder="Enter Phone"
              className={`${errors.phone ? "border-red-500" : ""}`}
              {...register("phone", {
                pattern: {
                  value: /^\d{10}$/,
                  message: "Phone must be 10 numeric characters",
                },
              })}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm">
                {errors.phone.message as string}
              </p>
            )}
          </div>
        )}

        {(!showMissingFieldsOnly || !birthdate) && (
          <div className="flex flex-col gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <div
                className={`p-2 bg-purple-50 rounded-lg flex items-center justify-center`}
              >
                <Cake className="w-4 h-4 text-purple-500" />
              </div>
              <h4 className="text-md font-semibold">Birthdate</h4>
            </div>
            <Controller
              name="birthdate"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value) return "Birthdate is required";

                  const today = new Date();

                  if (value.getFullYear() === today.getFullYear()) {
                    return "Invalid birthdate";
                  }
                },
              }}
              render={({ field }) => {
                const [openDate, setOpenDate] = useState(false);
                return (
                  <Popover open={openDate} onOpenChange={setOpenDate}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        id="date"
                        className={`w-full justify-between font-normal ${
                          errors.birthdate ? "border-red-500" : ""
                        }`}
                      >
                        {field.value
                          ? field.value.toLocaleDateString("en-GB")
                          : "Select date"}
                        <ChevronDownIcon />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto overflow-hidden p-0"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={field.value}
                        captionLayout="dropdown"
                        onSelect={(date) => {
                          field.onChange(date);
                          setOpenDate(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                );
              }}
            />
            {errors.birthdate && (
              <p className="text-red-500 text-sm">
                {errors.birthdate.message as string}
              </p>
            )}
          </div>
        )}
        <Button
          type="submit"
          size="lg"
          className="mt-auto shrink-0 mb-2"
          disabled={loading}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>

        {showSkipButton && (
          <Button size="lg" className="shrink-0 mb-2" onClick={onClose}>
            Skip
          </Button>
        )}
      </form>
    );
  };
  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      renderContent={renderContent}
    />
  );
}
